import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import dayjs from 'dayjs';
import {
  AppButton,
  AppCheckbox,
  AppForm,
  AppSelect,
  AppTextArea,
  AppTextField,
} from '@/components/atoms';
import { DeleteFill, Plus } from '@/components/atoms/AppIcon';
import { RA2NDatePicker } from '@/components/organisms';
import { convGtJDate, dateValueToIso } from '@/utils/dateConverter';
import {
  orderRezeptSheetEditSchema,
  type OrderRezeptSheetEditFormData,
} from '@/pages/admin/admin-order-rezept-sheets/orderRezeptSheetEditSchema';
import type {
  OrderRezeptSheetDetailItem,
  RezeptSheetOption,
  UpdateOrderRezeptSheetPayload,
} from '@/services/admin/order-rezept-sheets';

/** Convert ISO YYYY-MM-DD -> wareki display string */
function isoToWareki(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const d = dayjs(dateStr);
  return d.isValid() ? convGtJDate(d) : dateStr;
}

const fmtNum = (n: number | null | undefined): string => {
  if (n == null) return '';
  return n.toLocaleString('ja-JP');
};

type Props = {
  initialDetails: OrderRezeptSheetDetailItem[];
  rezeptSheets: RezeptSheetOption[];
  subtotal: number;
  deliveryFeeTotal: number;
  total: number;
  onSubmit: (data: UpdateOrderRezeptSheetPayload) => void;
  isSubmitting: boolean;
  onCancel: () => void;
  onDirtyChange?: (dirty: boolean) => void;
};

const MAX_DETAIL_ROWS = 30;

const QUANTITY_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map(
  n => ({ label: String(n), value: String(n) })
);

const OrderRezeptSheetEditForm: React.FC<Props> = ({
  initialDetails,
  rezeptSheets,
  subtotal,
  deliveryFeeTotal,
  total,
  onSubmit,
  isSubmitting,
  onCancel,
  onDirtyChange,
}) => {
  const defaultValues: OrderRezeptSheetEditFormData = useMemo(
    () => ({
      details: initialDetails.map(d => ({
        id: d.id,
        rezept_sheet_id: d.rezept_sheet_id,
        quantity: d.quantity != null ? String(d.quantity) : '',
        delivery_fee: d.delivery_fee != null ? String(d.delivery_fee) : '',
        is_include_case: d.is_include_case,
        request_delivery_date: d.request_delivery_date ?? null,
        ship_date: d.ship_date ?? null,
        remarks: d.remarks ?? null,
        is_delete: false,
      })),
    }),
    [initialDetails]
  );

  const {
    control,
    handleSubmit,
    getValues,
    watch,
    clearErrors,
    formState: { isDirty },
  } = useTrimmedForm<OrderRezeptSheetEditFormData>({
    resolver: zodResolver(orderRezeptSheetEditSchema),
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  // Clear field error as soon as user edits that field
  useEffect(() => {
    const subscription = watch((_, { name }) => {
      if (!name) return;
      clearErrors(name);
      // Cross-field: editing 納品希望日 also clears the ship_date error on the same row
      const m = name.match(/^details\.(\d+)\.request_delivery_date$/);
      if (m) clearErrors(`details.${m[1]}.ship_date` as Parameters<typeof clearErrors>[0]);
    });
    return () => subscription.unsubscribe();
  }, [watch, clearErrors]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'details',
  });

  // Track deleted existing detail IDs (rows with id that were removed from UI)
  const [deletedIds, setDeletedIds] = useState<number[]>([]);

  // Unsaved changes: isDirty covers field edits + add rows; deletedIds covers removed rows
  const hasUnsavedChanges = isDirty || deletedIds.length > 0;

  useEffect(() => {
    onDirtyChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onDirtyChange]);

  const rezeptSheetOptions = useMemo(
    () =>
      rezeptSheets.map(rs => ({
        label: `${rs.sheet_name}${rs.unit_amount ?? ''}枚 ${rs.price}円`,
        value: rs.id,
      })),
    [rezeptSheets]
  );

  const handleAddRow = useCallback(() => {
    if (fields.length >= MAX_DETAIL_ROWS) return;
    append({
      id: null,
      rezept_sheet_id: null,
      quantity: '',
      delivery_fee: '',
      is_include_case: false,
      request_delivery_date: null,
      ship_date: null,
      remarks: null,
      is_delete: false,
    });
  }, [fields.length, append]);

  const onWarekiDateChange = (fieldOnChange: (v: string | null) => void) => (warekiStr: string) => {
    if (!warekiStr?.trim()) {
      fieldOnChange(null);
      return;
    }
    const iso = dateValueToIso(warekiStr);
    fieldOnChange(iso || null);
  };

  const handleDeleteRow = useCallback(
    (index: number) => {
      const formValues = getValues('details');
      const dbId = formValues?.[index]?.id;
      if (dbId != null) {
        setDeletedIds(prev => [...prev, dbId]);
      }
      remove(index);
    },
    [getValues, remove]
  );

  /** Row has no 用紙名 selected */
  const hasNoSheet = (d: OrderRezeptSheetEditFormData['details'][number]) =>
    d.rezept_sheet_id == null || d.rezept_sheet_id <= 0;

  const onFormSubmit = useCallback(
    (data: OrderRezeptSheetEditFormData) => {
      const allDeletedIds = [...deletedIds];
      const removeIndices: number[] = [];

      data.details.forEach((d, i) => {
        if (hasNoSheet(d)) {
          if (d.id != null && !allDeletedIds.includes(d.id)) {
            allDeletedIds.push(d.id);
          }
          removeIndices.push(i);
        }
      });

      // Remove empty rows from UI (last to first)
      if (removeIndices.length > 0) {
        for (let i = removeIndices.length - 1; i >= 0; i--) {
          remove(removeIndices[i]);
        }
      }

      // Parse string → number for API payload
      const filtered = data.details
        .filter(d => !hasNoSheet(d))
        .map(d => ({
          ...d,
          quantity: parseInt(d.quantity, 10) || 0,
          delivery_fee: parseInt(d.delivery_fee, 10) || 0,
        }));

      // Append all deleted rows (button delete + cleared existing rows)
      const deletedRows = allDeletedIds.map(dbId => ({
        id: dbId,
        rezept_sheet_id: null,
        quantity: 0,
        delivery_fee: 0,
        is_include_case: false,
        is_delete: true,
      }));

      onSubmit({ details: [...filtered, ...deletedRows] });
    },
    [onSubmit, deletedIds, remove]
  );

  return (
    <form onSubmit={handleSubmit(onFormSubmit)}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b bg-gray-50">
              <th className="w-[40px] px-2 py-2 text-center font-medium">No.</th>
              <th className="w-[250px] min-w-[250px] max-w-[250px] px-2 py-2 text-left font-medium">
                用紙名
              </th>
              <th className="min-w-[80px] px-2 py-2 text-left font-medium">数量</th>
              <th className="min-w-[100px] px-2 py-2 text-left font-medium">送料</th>
              <th className="w-[60px] px-2 py-2 text-center font-medium">ケース</th>
              <th className="min-w-[160px] px-2 py-2 text-left font-medium">納品希望日</th>
              <th className="min-w-[140px] px-2 py-2 text-left font-medium">備考</th>
              <th className="min-w-[160px] px-2 py-2 text-left font-medium">出荷日</th>
              <th className="w-[50px] px-2 py-2 text-center font-medium">削除</th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field, index) => (
              <tr key={field.id} className="border-b">
                {/* No. */}
                <td className="px-2 py-2 text-center align-top">
                  <p className="mt-2.5">{index + 1}</p>
                </td>

                {/* 用紙名 dropdown */}
                <td className="w-[250px] min-w-[250px] max-w-[250px] px-2 py-2 align-top">
                  <Controller
                    name={`details.${index}.rezept_sheet_id`}
                    control={control}
                    render={({ field: f, fieldState }) => {
                      const allDetails = getValues('details');
                      const sheetCount = allDetails.filter(
                        d => d.rezept_sheet_id != null && d.rezept_sheet_id > 0
                      ).length;
                      const isLastWithSheet = f.value != null && f.value > 0 && sheetCount <= 1;

                      return (
                        <AppForm.Item
                          validateStatus={fieldState.error ? 'error' : undefined}
                          help={fieldState.error?.message}
                          className="mb-0"
                        >
                          <AppSelect
                            {...f}
                            value={f.value ?? undefined}
                            onChange={v => f.onChange(v ?? null)}
                            options={rezeptSheetOptions}
                            placeholder="---"
                            allowClear={!isLastWithSheet}
                            disabled={isSubmitting}
                            className="w-full"
                            status={fieldState.error ? 'error' : undefined}
                          />
                        </AppForm.Item>
                      );
                    }}
                  />
                </td>

                {/* 数量 */}
                <td className="px-2 py-2 align-top">
                  <Controller
                    name={`details.${index}.quantity`}
                    control={control}
                    render={({ field: f, fieldState }) => (
                      <AppForm.Item
                        validateStatus={fieldState.error ? 'error' : undefined}
                        help={fieldState.error?.message}
                        className="mb-0"
                      >
                        <AppSelect
                          value={f.value ? f.value : undefined}
                          onChange={v => f.onChange(v ?? '')}
                          options={QUANTITY_OPTIONS}
                          placeholder="---"
                          disabled={isSubmitting}
                          className="w-full"
                          status={fieldState.error ? 'error' : undefined}
                        />
                      </AppForm.Item>
                    )}
                  />
                </td>

                {/* 送料 + 円 suffix */}
                <td className="px-2 py-2 align-top">
                  <Controller
                    name={`details.${index}.delivery_fee`}
                    control={control}
                    render={({ field: f, fieldState }) => (
                      <AppForm.Item
                        validateStatus={fieldState.error ? 'error' : undefined}
                        help={fieldState.error?.message}
                        className="mb-0"
                      >
                        <div className="flex items-center gap-1">
                          <AppTextField
                            {...f}
                            value={f.value ?? ''}
                            disabled={isSubmitting}
                            className="w-full"
                            status={fieldState.error ? 'error' : undefined}
                          />
                          <span className="shrink-0">円</span>
                        </div>
                      </AppForm.Item>
                    )}
                  />
                </td>

                {/* ケース */}
                <td className="px-2 py-2 text-center align-top">
                  <div className="flex h-8 items-center justify-center">
                    <Controller
                      name={`details.${index}.is_include_case`}
                      control={control}
                      render={({ field: f }) => (
                        <AppCheckbox
                          checked={!!f.value}
                          onChange={e => f.onChange(e.target.checked)}
                          disabled={isSubmitting}
                        />
                      )}
                    />
                  </div>
                </td>

                {/* 納品希望日 */}
                <td className="px-2 py-2 align-top">
                  <Controller
                    name={`details.${index}.request_delivery_date`}
                    control={control}
                    render={({ field: f }) => (
                      <RA2NDatePicker
                        picker="day"
                        value={isoToWareki(f.value)}
                        onChange={onWarekiDateChange(f.onChange)}
                        placeholder=""
                        disabled={isSubmitting}
                        className="w-full"
                      />
                    )}
                  />
                </td>

                {/* 備考 */}
                <td className="px-2 py-2 align-top">
                  <Controller
                    name={`details.${index}.remarks`}
                    control={control}
                    render={({ field: f, fieldState }) => (
                      <AppForm.Item
                        validateStatus={fieldState.error ? 'error' : undefined}
                        help={fieldState.error?.message}
                        className="mb-0"
                      >
                        <AppTextArea
                          value={f.value ?? ''}
                          onChange={e => f.onChange(e.target.value || null)}
                          maxLength={225}
                          rows={1}
                          disabled={isSubmitting}
                          className="resize-y"
                        />
                      </AppForm.Item>
                    )}
                  />
                </td>

                {/* 出荷日 */}
                <td className="px-2 py-2 align-top">
                  <Controller
                    name={`details.${index}.ship_date`}
                    control={control}
                    render={({ field: f, fieldState }) => (
                      <AppForm.Item
                        validateStatus={fieldState.error ? 'error' : undefined}
                        help={fieldState.error?.message}
                        className="mb-0"
                      >
                        <RA2NDatePicker
                          picker="day"
                          value={isoToWareki(f.value)}
                          onChange={onWarekiDateChange(f.onChange)}
                          placeholder=""
                          disabled={isSubmitting}
                          className="w-full"
                          error={!!fieldState.error}
                        />
                      </AppForm.Item>
                    )}
                  />
                </td>

                {/* 削除 button */}
                <td className="px-2 py-2 text-center align-top">
                  <div className="flex h-8 items-center justify-center">
                    {fields.length > 1 && (
                      <AppButton
                        type="text"
                        danger
                        icon={<DeleteFill />}
                        onClick={() => handleDeleteRow(index)}
                        disabled={isSubmitting}
                        aria-label="削除"
                      />
                    )}
                  </div>
                </td>
              </tr>
            ))}

            {/* Summary row */}
            <tr className="border-t-2 border-gray-400 bg-gray-100">
              <td />
              <td className="px-2 py-2 font-bold">合計</td>
              <td className="px-2 py-2 text-right font-bold">{fmtNum(subtotal)}円</td>
              <td className="px-2 py-2 text-right font-bold">{fmtNum(deliveryFeeTotal)}円</td>
              <td />
              <td />
              <td />
              <td className="px-2 py-2 text-right font-bold">{fmtNum(total)}円</td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>

      {/* Add row button */}
      {fields.length < MAX_DETAIL_ROWS && (
        <div className="mt-3">
          <AppButton type="primary" icon={<Plus />} onClick={handleAddRow} disabled={isSubmitting}>
            追加
          </AppButton>
        </div>
      )}

      {/* Action buttons */}
      <div className="mt-6 flex items-center gap-3 justify-end">
        <AppButton className="px-10" type="primary" htmlType="submit" loading={isSubmitting}>
          更新
        </AppButton>
        <AppButton onClick={onCancel} disabled={isSubmitting}>
          前画面に戻る
        </AppButton>
      </div>
    </form>
  );
};

export default OrderRezeptSheetEditForm;
